import { useEffect, useState } from 'react';

/**
 * Tracks the rendered height of each stacked toast and returns
 * `getOffsetFromIndex(index)` — the cumulative height of every toast above
 * `index` — used to vertically stack the absolutely-positioned `<li>`s.
 *
 * Ported from the legacy `useNotificationAnimation.ts`:
 * - a MutationObserver watches the `<ul>` for added/removed `<li>`s;
 * - on add, the new toast height is prepended to the offset array (the
 *   stack renders newest-first, so the newest toast sits at the top);
 * - on remove, the removed offset is dropped and the removed element is
 *   cloned with `data-index="-1"` so it can run the exit transition; the
 *   clone removes itself on the opacity `transitionend`.
 *
 * @param {React.RefObject<HTMLUListElement>} ref
 * @returns {(index: number) => number}
 */
export function useNotificationAnimation(ref) {
    const [offsetArray, setOffsetArray] = useState([]);

    useEffect(() => {
        if (ref.current) {
            const config = { attributes: false, childList: true, subtree: true };

            // Callback to execute when mutations are observed
            const callback = (mutationList) => {
                for (const mutation of mutationList) {
                    if (mutation.type === 'childList') {
                        // Node added
                        if (mutation.addedNodes.length > 0) {
                            const el = mutation.addedNodes[0];
                            if (el.dataset.index === '-1') {
                                // ignore cloned element
                                return;
                            }
                            const height = el.offsetHeight;
                            setOffsetArray(o => [height].concat(o));
                        }
                        // Node removed
                        if (mutation.removedNodes.length > 0) {
                            const el = mutation.removedNodes[0];
                            const index = Number(el.dataset.index);

                            if (index === -1) {
                                // ignore cloned element
                                return;
                            }
                            setOffsetArray(a => a.filter((_, i) => i !== index));

                            const cloned = el.cloneNode(true);
                            cloned.dataset.index = '-1';
                            cloned.addEventListener('transitionend', function (event) {
                                // Multiple transitions — only listen to opacity
                                if (event.propertyName === 'opacity') {
                                    this.remove();
                                }
                            });

                            ref.current?.append(cloned);
                        }
                    }
                }
            };

            const observer = new MutationObserver(callback);
            observer.observe(ref.current, config);

            return () => {
                observer.disconnect();
            };
        }
    }, []);

    return function getOffsetFromIndex(index) {
        return offsetArray.slice(0, index).reduce((acc, o) => acc + o, 0);
    };
}

export default useNotificationAnimation;